/**
 * Copyright (c) 2025 rports (r), All rights reserved.
 *
 *     ______    _______  _______  ______    _______  _______ 
 *    |    _ |  |       ||       ||    _ |  |       ||       |
 *    |   | ||  |    _  ||   _   ||   | ||  |_     _||  _____|
 *    |   |_||_ |   |_| ||  | |  ||   |_||_   |   |  | |_____ 
 *    |    __  ||    ___||  |_|  ||    __  |  |   |  |_____  |
 *    |   |  | ||   |    |       ||   |  | |  |   |   _____| |
 *    |___|  |_||___|    |_______||___|  |_|  |___|  |_______|
 *
 * filename      : NavBar.jsx
 * description   : {{insert here}}
 * creation date : 13/04/2025
 * author        : "Luis CRUZ" <luis.cruz@rports.net>
 */



// --------------------------------------------------------------------------------
//      IMPORTS
// --------------------------------------------------------------------------------

import {
    Fragment,
} from 'react';

import {
    Flex,
    View,
} from '@aws-amplify/ui-react';

import {
    route_list,
    id_list,
} from '@/utils/router/core';

import {
    useComponentContext,
    toolKit,
} from '@/components/core';

import {
    RouterLink,
    LinkButton,
} from '@/components/snippets/RouterLink';

import {
    ButtonNavigateToSignIn,
    ButtonSignOut,
} from '@/components/snippets/CustomDiv';

import {
    BrandLogo,
    brand_logo_variant,
} from '@/components/snippets/BrandLogo';

import {
    NavBarItem,
} from '@/components/snippets/NavBarItem';

import {
    NavBarSideBar,
} from '@/components/snippets/NavBarSideBar';

import {
    FaUserCircle as IconUser,
} from 'react-icons/fa';

import Announcement from '@/components/Announcement';



// --------------------------------------------------------------------------------
//      FUNCTIONS
// --------------------------------------------------------------------------------

function NavBar({ classPrefix, configPages, logoColor }) {
    // Component context
    const { ctx } = useComponentContext();

    // Configuration
    const config_flexContainer =
    {
        [toolKit.enums.window.viewPortTypes.LARGE]:
        {
            paddingTop: ctx.theme.tokens.space.zero,
            paddingBottom: ctx.theme.tokens.space.zero,
            paddingLeft: ctx.theme.tokens.space.medium,
            paddingRight: ctx.theme.tokens.space.medium,
        },

        [toolKit.enums.window.viewPortTypes.MEDIUM]:
        {
            paddingTop: ctx.theme.tokens.space.zero,
            paddingBottom: ctx.theme.tokens.space.zero,
            paddingLeft: ctx.theme.tokens.space.medium,
            paddingRight: ctx.theme.tokens.space.medium,
        },

        [toolKit.enums.window.viewPortTypes.SMALL]:
        {
            paddingTop: ctx.theme.tokens.space.zero,
            paddingBottom: ctx.theme.tokens.space.zero,
            paddingLeft: ctx.theme.tokens.space.medium,
            paddingRight: ctx.theme.tokens.space.xs,
        },
    };

    const config_navbarItems =
    {
        [toolKit.enums.window.viewPortTypes.LARGE]:
        {
            primary:
            {
                height: 'var(--amplify-custom-components-navbar-item-height-view-port-large-primary)',
            },

            secondary:
            {
                height: 'var(--amplify-custom-components-navbar-item-height-view-port-large-secondary)',
                fontSize: ctx.theme.tokens.fontSizes.medium,
                fontWeight: ctx.theme.tokens.fontWeights.light,
                paddingLeft: ctx.theme.tokens.space.medium,
                paddingRight: ctx.theme.tokens.space.medium,
                isSelectable: true,
            },
        },

        [toolKit.enums.window.viewPortTypes.MEDIUM]:
        {
            primary:
            {
                height: 'var(--amplify-custom-components-navbar-item-height-view-port-medium-primary)',
            },

            secondary:
            {
                height: 'var(--amplify-custom-components-navbar-item-height-view-port-medium-secondary)',
                fontSize: ctx.theme.tokens.fontSizes.medium,
                fontWeight: ctx.theme.tokens.fontWeights.light,
                paddingLeft: ctx.theme.tokens.space.medium,
                paddingRight: ctx.theme.tokens.space.medium,
                isSelectable: true,
            },
        },

        [toolKit.enums.window.viewPortTypes.SMALL]:
        {
            primary:
            {
                height: 'var(--amplify-custom-components-navbar-item-height-view-port-small-primary)',
            },

            secondary:
            {
                // N.A.
            },
        },
    };

    const config_brandLogo =
    {
        [toolKit.enums.window.viewPortTypes.LARGE]:
        {
            height: ctx.theme.tokens.space.large,
            logoVariant: brand_logo_variant.squareRounded,
            logoColor: logoColor,
            marginRight: ctx.theme.tokens.space.large,
        },

        [toolKit.enums.window.viewPortTypes.MEDIUM]:
        {
            height: ctx.theme.tokens.space.large,
            logoVariant: brand_logo_variant.squareRounded,
            logoColor: logoColor,
            marginRight: ctx.theme.tokens.space.large,
        },

        [toolKit.enums.window.viewPortTypes.SMALL]:
        {
            height: ctx.theme.tokens.space.large,
            logoVariant: brand_logo_variant.squareRounded,
            logoColor: logoColor,
        },
    };

    // Populate configuration pages
    function ConfigPages() {
        // View port: Large & Medium
        if (
            (ctx.runtime.screen.viewPort === toolKit.enums.window.viewPortTypes.LARGE) ||
            (ctx.runtime.screen.viewPort === toolKit.enums.window.viewPortTypes.MEDIUM)
        ) {
            return (
                <Fragment>
                    {configPages.map((page, i) => {
                        // Detect private access
                        if (false === ctx.api.location.canBeDisplayed(page)) {
                            return null;
                        }

                        // Hash
                        const hash_ref = toolKit.api.location.serializeHash(page.hash);
                        const hash_cur = toolKit.api.location.serializeHash(ctx.api.location.getHash());

                        // Target URL
                        const to = toolKit.api.location.buildUrl(
                            page.path,
                            page.search,
                            page.hash
                        );

                        // Is active
                        const isActive = (
                            (ctx.api.location.getPath() === page.path) &&
                            (hash_cur === hash_ref)
                        );

                        return (
                            <Fragment
                                key={i}
                            >
                                <RouterLink
                                    to={to}
                                    className={`${classPrefix}-item-link`}
                                >
                                    <NavBarItem
                                        classPrefix={classPrefix}
                                        {...config_navbarItems[ctx.runtime.screen.viewPort].secondary}
                                        isActive={isActive}
                                    >
                                        {page.label.navbar}
                                    </NavBarItem>
                                </RouterLink>
                            </Fragment>
                        );
                    })}
                </Fragment>
            );
        }

        // View port: Small
        else if (ctx.runtime.screen.viewPort === toolKit.enums.window.viewPortTypes.SMALL) {
            var pages = [];

            for (const page of configPages) {
                if (ctx.api.location.canBeDisplayed(page)) {
                    pages.push(
                        {
                            to: toolKit.api.location.buildUrl(
                                page.path,
                                page.search,
                                page.hash
                            ),
                            label: page.label.navbar,
                        }
                    );
                }
            }

            return pages;
        }

        // Otherwise
        else {
            return (
                <Fragment />
            );
        }
    }

    return (
        <Fragment>
            <View
                id={id_list.NAVBAR}
                className={`${classPrefix}`}
            >
                <Announcement />

                <View
                    className={`${classPrefix}-container`}
                >
                    <Flex
                        justifyContent='space-between'
                        alignItems='center'
                        alignContent='center'
                        gap={ctx.theme.tokens.space.zero}
                        color={ctx.theme.tokens.colors.font.inverse}
                        width={ctx.theme.tokens.space.relative.full}
                        {...config_flexContainer[ctx.runtime.screen.viewPort]}
                    >
                        {/* Left side */}
                        <Flex
                            justifyContent='flex-start'
                            alignItems='center'
                            alignContent='center'
                            direction='row'
                            wrap='nowrap'
                            gap={ctx.theme.tokens.space.zero}
                        >
                            <NavBarItem
                                classPrefix={classPrefix}
                                {...config_navbarItems[ctx.runtime.screen.viewPort].primary}
                            >
                                <BrandLogo
                                    {...config_brandLogo[ctx.runtime.screen.viewPort]}
                                />
                            </NavBarItem>

                            {/* View port: Medium & Large */}
                            {(
                                (ctx.runtime.screen.viewPort === toolKit.enums.window.viewPortTypes.LARGE) ||
                                (ctx.runtime.screen.viewPort === toolKit.enums.window.viewPortTypes.MEDIUM)
                            ) &&
                                <Fragment>
                                    <ConfigPages />
                                </Fragment>
                            }
                        </Flex>

                        {/* Right side */}
                        <Flex
                            justifyContent='flex-end'
                            alignItems='center'
                            alignContent='center'
                            direction='row'
                            wrap='nowrap'
                            gap={ctx.theme.tokens.space.xs}
                        >
                            {/* View port: Medium & Large */}
                            {(
                                (ctx.runtime.screen.viewPort === toolKit.enums.window.viewPortTypes.LARGE) ||
                                (ctx.runtime.screen.viewPort === toolKit.enums.window.viewPortTypes.MEDIUM)
                            ) &&
                                <Fragment>
                                    <NavBarItem
                                        classPrefix={classPrefix}
                                        {...config_navbarItems[ctx.runtime.screen.viewPort].primary}
                                    >
                                        <LinkButton
                                            to={
                                                toolKit.api.location.buildUrl(
                                                    route_list.ACCOUNT
                                                )
                                            }
                                            size='medium'
                                            variation='primary'
                                            className={`${classPrefix}-button-icon`}
                                        >
                                            <IconUser
                                                size={ctx.theme.tokens.space.large.value}
                                            />
                                        </LinkButton>
                                    </NavBarItem>

                                    {/* Is authenticated */}
                                    {(ctx.auth.state === toolKit.enums.auth.stateTypes.AUTHENTICATED) &&
                                        <Fragment>
                                            <NavBarItem
                                                classPrefix={classPrefix}
                                                {...config_navbarItems[ctx.runtime.screen.viewPort].primary}
                                            >
                                                <ButtonSignOut
                                                    size='small'
                                                    variation='primary'
                                                    className={`${classPrefix}-button-auth`}
                                                />
                                            </NavBarItem>
                                        </Fragment>
                                    }

                                    {/* Is NOT authenticated */}
                                    {(ctx.auth.state === toolKit.enums.auth.stateTypes.UNAUTHENTICATED) &&
                                        <Fragment>
                                            <NavBarItem
                                                classPrefix={classPrefix}
                                                {...config_navbarItems[ctx.runtime.screen.viewPort].primary}
                                            >
                                                <ButtonNavigateToSignIn
                                                    size='small'
                                                    variation='primary'
                                                    className={`${classPrefix}-button-auth`}
                                                />
                                            </NavBarItem>
                                        </Fragment>
                                    }
                                </Fragment>
                            }

                            {/* View port: Small */}
                            {(ctx.runtime.screen.viewPort === toolKit.enums.window.viewPortTypes.SMALL) &&
                                <Fragment>
                                    <NavBarItem
                                        classPrefix={classPrefix}
                                        {...config_navbarItems[ctx.runtime.screen.viewPort].primary}
                                    >
                                        <NavBarSideBar
                                            classPrefix={classPrefix}
                                            paddingTop={config_navbarItems[ctx.runtime.screen.viewPort].primary.height}
                                            itemsTop={
                                                ConfigPages()
                                            }
                                            itemsBottom={
                                                [
                                                    {
                                                        to: toolKit.api.location.buildUrl(
                                                            route_list.ACCOUNT
                                                        ),
                                                        label: 'Account', // TODO: Translate message
                                                    },
                                                    {
                                                        isAuth: true,
                                                    },
                                                ]
                                            }
                                        />
                                    </NavBarItem>
                                </Fragment>
                            }
                        </Flex>
                    </Flex>
                </View>
            </View>
        </Fragment>
    );
}



// --------------------------------------------------------------------------------
//      EXPORTED OBJECTS
// --------------------------------------------------------------------------------

export default NavBar;



// End of file
